import type { EventMarketDto } from '@/api/queries';
import type { BetSettings, CashoutSettings } from '@/api/auth';
import { getCombinationSets } from '@/domain/betslip';
import {
  TicketStatus,
  TicketType,
  type TicketListItem,
} from '@/domain/tickets';
import { parseApiUtcDate } from '@/lib/date';

/** Hide offer below this amount (legacy cashout.js). */
export const MIN_CASHOUT_OFFER = 0.1;

/** Partial cashout slider bounds (legacy ionRangeSlider). */
export const PARTIAL_CASHOUT_MIN_PERCENT = 10;
export const PARTIAL_CASHOUT_MAX_PERCENT = 90;
export const PARTIAL_CASHOUT_STEP = 5;

export const CashoutModes = {
  Fair: 0,
  Managed: 1,
} as const;

/**
 * Managed peak extra margin % by CashoutSpeedLevel
 * (UsersAPI `Enums.CashoutLevels`: Fastest…Slowest — 5 levels).
 */
export const MANAGED_PEAK_MARGINS = [10, 8, 6, 4, 2] as const;
/** Managed edge margin % by CashoutSpeedLevel. */
export const MANAGED_EDGE_MARGINS = [0.5, 0.75, 1, 1.5, 2] as const;

export interface CashoutBet {
  Market?: string | null;
  MarketId?: string | null;
  Specifier?: string | null;
  Odd?: number | null;
  Outcome?: string | null;
  OutcomeId?: string | null;
  Won?: boolean | null;
  Void?: number | null;
  Probability?: number | null;
  Custom?: boolean;
  Selections?: unknown[] | null;
}

export interface CashoutTicketItem {
  Event: {
    EventId: number;
    Live?: boolean;
    Banked?: boolean;
  };
  Bets: CashoutBet[];
}

/** Open ticket shape sent to SportsAPI `cashoutlist` and kept for live updates. */
export interface OpenCashoutTicket {
  Id: string;
  Type: number;
  SystemMin?: number;
  Live?: boolean;
  Rows?: number;
  Tips?: number;
  Stake: number;
  Odds: number;
  PossibleWin: number;
  Probability: number | null;
  Items: CashoutTicketItem[];
  /**
   * Placed odds keyed by event|market|specifier|outcome (legacy `PlacedOdds`).
   * Survives cashoutlist Item replacement so system cashout can still price legs.
   */
  PlacedOdds?: Record<string, number>;
  TicketGuid?: string | null;
  CashoutWin?: number | null;
  InitialStake?: number;
}

/** One row in the Cash Out History panel (API partials + optional session). */
export interface CashoutHistoryEntry {
  id?: number | string;
  stake: number;
  amount: number;
  at: number;
  /** Present for in-session partials before the ticket list refreshes. */
  percentage?: number;
}

function historyDateMs(value: string | number | null | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    // Same UTC parsing as Sports / My Bets ticket dates (timezone-less = UTC).
    const ms = parseApiUtcDate(value).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return 0;
}

function historyRowKey(row: Pick<CashoutHistoryEntry, 'stake' | 'amount'>): string {
  return `${row.stake.toFixed(2)}:${row.amount.toFixed(2)}`;
}

/**
 * Build Cash Out History rows from the ticket list DTO (legacy
 * `CashoutHistoryBefore` with `PartialCashoutHistory`).
 * Sold tickets append the final full-cashout remainder when present.
 */
export function cashoutHistoryFromTicket(ticket: TicketListItem): CashoutHistoryEntry[] {
  const partials = ticket.PartialCashoutHistory ?? [];
  const rows: CashoutHistoryEntry[] = partials.map((row, index) => ({
    id: row.Id ?? `api-${index}`,
    stake: toNumber(row.Stake, 0),
    amount: toNumber(row.Win, 0),
    at: historyDateMs(row.Date),
  }));

  if (rows.length === 0) return rows;

  const status = Number(ticket.Status);
  if (status === TicketStatus.Sold) {
    const partialWins = rows.reduce((sum, row) => sum + row.amount, 0);
    const cashoutWin = toNumber(ticket.CashoutWin, 0);
    const remainder = Math.max(0, Math.round((cashoutWin - partialWins) * 100) / 100);
    if (remainder > 0 || cashoutWin > partialWins) {
      rows.push({
        id: 'full-cashout',
        stake: toNumber(ticket.Stake, 0),
        amount: remainder > 0 ? remainder : Math.max(0, cashoutWin - partialWins),
        at: historyDateMs(ticket.WinTimestamp ?? ticket.Timestamp ?? ticket.Date),
      });
    }
  }

  return rows;
}

/**
 * API history plus in-session partials not yet on the ticket.
 * Match by stake+win (not client vs server time) so a list refresh does not
 * leave duplicate "ghost" rows after a partial cashout.
 */
export function mergeCashoutHistory(
  ticket: TicketListItem,
  session: CashoutHistoryEntry[] | undefined,
): CashoutHistoryEntry[] {
  const fromTicket = cashoutHistoryFromTicket(ticket);
  const sessionRows = session ?? [];
  if (sessionRows.length === 0) return fromTicket;
  if (fromTicket.length === 0) return sessionRows;

  const covered = new Set(fromTicket.map(historyRowKey));
  const pending = sessionRows.filter((row) => !covered.has(historyRowKey(row)));
  return pending.length ? [...fromTicket, ...pending] : fromTicket;
}

export function totalCashoutHistoryWin(entries: CashoutHistoryEntry[]): number {
  return entries.reduce((sum, row) => sum + row.amount, 0);
}

function isPositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function toNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Same as UsersAPI `RoundOdd` / legacy `Profile.RoundOdd`. */
export function roundOdd(odd: number): number {
  return Math.round(odd * 100) / 100;
}

/** Round current odds on cashoutlist / socket updates; leave 1 and null alone. */
export function roundTicketOdds(items: CashoutTicketItem[]): void {
  for (const item of items) {
    for (const bet of item.Bets) {
      if (bet.Odd != null && bet.Odd > 1) {
        bet.Odd = roundOdd(bet.Odd);
      }
      if (
        bet.Probability != null &&
        bet.Probability > 0 &&
        bet.Probability < 1
      ) {
        bet.Probability = 1 / roundOdd(1 / bet.Probability);
      }
    }
  }
}

function cartesianProduct(groups: number[][]): number[][] {
  if (groups.length === 0) return [[]];
  return groups.reduce<number[][]>(
    (acc, group) => acc.flatMap((prefix) => group.map((value) => [...prefix, value])),
    [[]],
  );
}

function calculateSingleProbability(items: CashoutTicketItem[]): number | null {
  let probability: number | null = null;
  for (const item of items) {
    for (const bet of item.Bets) {
      if (bet.Probability === null || bet.Probability === undefined) return null;
      probability = (probability ?? 0) + bet.Probability;
    }
  }
  if (probability === null || items.length === 0) return null;
  return probability / items.length;
}

function calculateMultiCombiProbability(items: CashoutTicketItem[]): number | null {
  const combiProbabilities: number[][] = [];
  for (const item of items) {
    const eventProbabilities: number[] = [];
    for (const bet of item.Bets) {
      if (bet.Probability === null || bet.Probability === undefined) return null;
      eventProbabilities.push(bet.Probability);
    }
    combiProbabilities.push(eventProbabilities);
  }
  const combinations = cartesianProduct(combiProbabilities);
  if (combinations.length === 0) return null;
  let probability = 0;
  for (const combo of combinations) {
    probability += combo.reduce((product, value) => product * value, 1);
  }
  return probability / combinations.length;
}

function calculateCombiProbability(items: CashoutTicketItem[]): number | null {
  let probability = 1;
  let tips = 0;
  for (const item of items) {
    for (const bet of item.Bets) {
      tips += 1;
      if (bet.Won === true && (bet.Void === null || bet.Void === undefined)) {
        continue;
      }
      if (bet.Probability === null || bet.Probability === undefined) return null;
      probability *= bet.Probability;
    }
  }
  if (tips === items.length) return probability;
  return calculateMultiCombiProbability(items);
}

function calculateSystemProbability(
  items: CashoutTicketItem[],
  systemMin: number,
): number | null {
  const unbanked: number[] = [];
  let banked = 1;
  for (const item of items) {
    const bet = item.Bets[0];
    if (!bet) return null;
    if (item.Event.Banked && (bet.Probability === null || bet.Probability === undefined)) {
      return null;
    }
    const betOdd = bet.Probability === 1 ? 1 : (bet.Odd ?? 0);
    if (item.Event.Banked) {
      banked *= betOdd;
    } else {
      unbanked.push(betOdd);
    }
  }
  const combinations = getCombinationSets(unbanked, systemMin);
  if (combinations.length === 0) return null;
  let systemProbability = 0;
  let aliveCombinations = 0;
  for (const combo of combinations) {
    const combinationProbability = combo.reduce((product, value) => product * value, 1);
    if (combinationProbability > 0) aliveCombinations += 1;
    systemProbability += combinationProbability;
  }
  const combinationsFactor =
    aliveCombinations === combinations.length
      ? combinations.length
      : aliveCombinations / combinations.length;
  if (combinationsFactor === 0) return null;
  return 1 / ((banked * systemProbability) / combinationsFactor);
}

/** Port of legacy `CalculateTicketProbability`. */
export function calculateTicketProbability(
  type: number,
  systemMin: number | undefined,
  items: CashoutTicketItem[],
): number | null {
  switch (type) {
    case TicketType.Single:
      return calculateSingleProbability(items);
    case TicketType.Combi:
      return calculateCombiProbability(items);
    case TicketType.System:
      return calculateSystemProbability(items, systemMin ?? 0);
    default:
      return null;
  }
}

/**
 * Port of UsersAPI `TicketsCashOutService.CalculateCashOut` / legacy `CalculateCashout`.
 * Offer is floored to 2 decimals and never above possibleWin.
 */
export function calculateCashout(
  settings: CashoutSettings | null | undefined,
  possibleWin: number,
  stake: number,
  newProbability: number | null | undefined,
  oldProbability: number | null | undefined,
): number {
  const cashoutMode = toNumber(settings?.CashoutMode, CashoutModes.Fair);
  const speedLevel = Math.floor(toNumber(settings?.CashoutSpeedLevel, 0));
  const profitPercent = toNumber(settings?.CashoutProfitPercent, 0);
  const stakeN = Number(stake);
  const possibleWinN = Number(possibleWin);
  if (
    !isPositive(stakeN) ||
    !isPositive(possibleWinN) ||
    !isPositive(newProbability) ||
    !isPositive(oldProbability)
  ) {
    return 0;
  }

  const value = stakeN * (newProbability / oldProbability);
  let margin = Math.min(100, Math.max(0, profitPercent)) / 100;

  if (cashoutMode === CashoutModes.Managed && possibleWinN > stakeN) {
    const level = Math.min(MANAGED_PEAK_MARGINS.length - 1, Math.max(0, speedLevel));
    const peakMargin = margin + MANAGED_PEAK_MARGINS[level]! / 100;
    const edgeMargin = Math.min(margin, MANAGED_EDGE_MARGINS[level]! / 100);
    const position = Math.min(1, Math.max(0, (value - stakeN) / (possibleWinN - stakeN)));
    margin = Math.min(1, edgeMargin + (peakMargin - edgeMargin) * 4 * position * (1 - position));
  }

  const offer = Math.min(value * (1 - margin), possibleWinN);
  return Math.floor((offer + Number.EPSILON) * 100) / 100;
}

function ticketHasBonus(ticket: TicketListItem): boolean {
  if (ticket.DepositBonus) return true;
  return Array.isArray(ticket.Bonuses) && ticket.Bonuses.length > 0;
}

function ticketHasCustomBet(ticket: TicketListItem): boolean {
  return Boolean(ticket.Bets?.some((bet) => bet.IsCustom));
}

/** Legacy OpenTickets eligibility (before cashoutlist / offer UI). */
export function isCashoutEligible(
  ticket: TicketListItem,
  cashoutSettings: CashoutSettings | null | undefined,
  betSettings: BetSettings | null | undefined,
): boolean {
  if (!cashoutSettings?.AllowCashout) return false;
  if (!betSettings?.AllowCashout) return false;
  if (!ticket.AllowCashout) return false;
  // Main ticket linked as KreditBet creditor — cashout disabled (legacy).
  if (ticket.Creditor === true) return false;
  if (ticket.HasRewards) return false;
  if (ticketHasCustomBet(ticket)) return false;
  if (ticketHasBonus(ticket) && !cashoutSettings.AllowCashoutBonus) return false;
  // CashoutMaximumOdds is applied at offer time on RemainingOpenOdds (not placed Odds).
  return true;
}

/** Bet key for PlacedOdds lookup — legacy `BetKey`. */
export function cashoutBetKey(
  eventId: number,
  bet: Pick<CashoutBet, 'MarketId' | 'Specifier' | 'Outcome'>,
): string {
  return [eventId, bet.MarketId, bet.Specifier ?? '', bet.Outcome]
    .map((value) => (value === null || value === undefined ? '' : String(value)))
    .join('|');
}

/** Snapshot placed odds before cashoutlist replaces Items — legacy `GetPlacedOdds`. */
export function getPlacedOdds(items: CashoutTicketItem[]): Record<string, number> {
  const placed: Record<string, number> = {};
  for (const item of items) {
    for (const bet of item.Bets) {
      const odd = Number(bet.Odd);
      if (isPositive(odd)) {
        placed[cashoutBetKey(item.Event.EventId, bet)] = roundOdd(odd);
      }
    }
  }
  return placed;
}

/**
 * System odds: banked * avg of SystemMin combinations of unbanked.
 * Legacy `SystemOdds` / UsersAPI `TicketCalculationUtilities.CalculateSystemOdds`.
 */
export function systemOdds(
  bankedOdds: number[],
  unbankedOdds: number[],
  systemMin: number,
): number {
  let banked = 1;
  for (const odd of bankedOdds) banked *= odd;
  const combinations = getCombinationSets(unbankedOdds, systemMin);
  if (combinations.length === 0) return banked;
  let systemOdd = 0;
  for (const combo of combinations) {
    systemOdd += combo.reduce((product, value) => product * value, 1);
  }
  return (banked * systemOdd) / combinations.length;
}

/**
 * Odds still open on the ticket (current odds of open legs; settled = 1).
 * Legacy / UsersAPI `RemainingOpenOdds`.
 */
export function remainingOpenOdds(ticket: OpenCashoutTicket): number {
  const legOdd = (bet: CashoutBet): number => {
    const open =
      bet.Won !== true &&
      bet.Probability !== null &&
      bet.Probability !== undefined &&
      bet.Probability > 0;
    return open ? roundOdd(1 / bet.Probability!) : 1;
  };

  if (ticket.Type === TicketType.System) {
    const banked: number[] = [];
    const unbanked: number[] = [];
    for (const item of ticket.Items) {
      const bet = item.Bets[0];
      if (!bet) continue;
      const odd = legOdd(bet);
      if (item.Event.Banked) banked.push(odd);
      else unbanked.push(odd);
    }
    return roundOdd(systemOdds(banked, unbanked, ticket.SystemMin ?? 0));
  }

  let odds = 1;
  for (const item of ticket.Items) {
    for (const bet of item.Bets) {
      odds *= legOdd(bet);
    }
  }
  return roundOdd(odds);
}

/** Legacy / UsersAPI `IsAboveCashoutMaximumOdds`. */
export function isAboveCashoutMaximumOdds(
  settings: CashoutSettings | null | undefined,
  remainingOdds: number,
): boolean {
  const maximumOdds = Number(settings?.CashoutMaximumOdds);
  return Number.isFinite(maximumOdds) && maximumOdds > 0 && remainingOdds > maximumOdds;
}

/**
 * System ticket value-per-stake at current odds (placed/current ratios).
 * Legacy / UsersAPI `SystemCashoutValueRatio`. Returns null when a leg cannot be priced.
 */
export function systemCashoutValueRatio(ticket: OpenCashoutTicket): number | null {
  const banked: number[] = [];
  const unbanked: number[] = [];
  for (const item of ticket.Items) {
    const bet = item.Bets[0];
    if (!bet) continue;
    const placed = ticket.PlacedOdds?.[cashoutBetKey(item.Event.EventId, bet)];
    if (!(placed != null && placed > 0)) return null;

    let ratio: number;
    if (bet.Won === true) {
      const voidFactor =
        bet.Void === null || bet.Void === undefined ? 0 : Number(bet.Void);
      ratio =
        voidFactor >= 1
          ? 1
          : voidFactor > 0
            ? (placed - 1) * voidFactor + 1
            : placed;
    } else {
      if (bet.Probability === null || bet.Probability === undefined || !(bet.Probability > 0)) {
        return null;
      }
      ratio = placed / roundOdd(1 / bet.Probability);
    }

    if (item.Event.Banked) banked.push(ratio);
    else unbanked.push(ratio);
  }
  return systemOdds(banked, unbanked, ticket.SystemMin ?? 0);
}

/**
 * Offer for an open ticket after Items are priced — legacy `GetTicketCashoutValue`.
 * Applies RemainingOpenOdds max-odds gate and system PlacedOdds ratio.
 */
export function getTicketCashoutValue(
  ticket: OpenCashoutTicket,
  settings: CashoutSettings | null | undefined,
): number {
  if (isAboveCashoutMaximumOdds(settings, remainingOpenOdds(ticket))) {
    return 0;
  }

  let newProbability: number | null;
  if (ticket.Type === TicketType.System) {
    const ratio = systemCashoutValueRatio(ticket);
    newProbability =
      ratio === null || !isPositive(ticket.Probability)
        ? null
        : ratio * ticket.Probability;
  } else {
    newProbability = calculateTicketProbability(
      ticket.Type,
      ticket.SystemMin,
      ticket.Items,
    );
  }

  return calculateCashout(
    settings,
    ticket.PossibleWin,
    ticket.Stake,
    newProbability,
    ticket.Probability,
  );
}

export function canShowPartialCashout(
  cashoutSettings: CashoutSettings | null | undefined,
  betSettings: BetSettings | null | undefined,
): boolean {
  return Boolean(betSettings?.AllowPartialCashout || cashoutSettings?.PartialCashout);
}

/** Placed ticket probability for cashout; fall back to 1/Odds when list omits it. */
export function placedTicketProbability(
  probability: number | null | undefined,
  odds: number | null | undefined,
): number | null {
  if (isPositive(probability)) return probability;
  if (isPositive(odds) && odds > 1) return 1 / odds;
  return null;
}

/** Build SportsAPI cashoutlist ticket from a list DTO. */
export function buildOpenCashoutTicket(ticket: TicketListItem): OpenCashoutTicket {
  const bets = ticket.Bets ?? [];
  const items: CashoutTicketItem[] = bets.map((bet) => ({
    Event: {
      EventId: bet.MatchId ?? 0,
      Live: bet.Live,
      Banked: bet.Banked,
    },
    Bets: [
      {
        Market: bet.Market,
        MarketId: bet.MarketId,
        Specifier: bet.Specifier,
        Odd: bet.Value,
        Outcome: bet.Outcome,
        OutcomeId: bet.OutcomeId,
        Won: bet.Won,
        Void: bet.Void,
        Custom: bet.IsCustom,
        Selections: bet.Selections,
      },
    ],
  }));

  return {
    Id: String(ticket.Id),
    Type: Number(ticket.Type),
    SystemMin: ticket.SystemMin,
    Live: ticket.Live ?? bets.some((bet) => bet.Live),
    Rows: ticket.Combinations,
    Tips: bets.length,
    Stake: ticket.Stake,
    Odds: ticket.Odds,
    PossibleWin: ticket.PossibleWin,
    Probability: placedTicketProbability(ticket.Probability, ticket.Odds),
    Items: items,
    PlacedOdds: getPlacedOdds(items),
    TicketGuid: ticket.TicketGuid,
    CashoutWin: ticket.CashoutWin,
    InitialStake: ticket.InitialStake ?? ticket.Stake,
  };
}

function normalizeSpecifier(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

function marketIdMatches(betMarketId: string | null | undefined, marketId: number | undefined): boolean {
  if (marketId === undefined || marketId === null) return false;
  return Number(betMarketId) === marketId;
}

/**
 * Apply a live odds patch to one open cashout ticket (legacy `UpdateCashoutFromSocket`).
 * Returns the new offer amount (0 when blocked).
 */
export function applyLiveOddsToCashoutTicket(
  ticket: OpenCashoutTicket,
  eventId: number,
  markets: EventMarketDto[] | undefined,
  active: boolean | undefined,
  settings: CashoutSettings | null | undefined,
): number {
  const eventActive = active !== false;
  const hasMarkets = Array.isArray(markets) && markets.length > 0;

  for (const item of ticket.Items) {
    if (item.Event.EventId !== eventId) continue;

    if (!eventActive || !hasMarkets) {
      for (const bet of item.Bets) {
        bet.Probability = null;
      }
      continue;
    }

    for (const bet of item.Bets) {
      let found = false;
      for (const market of markets!) {
        const lines = market.Lines ?? [];
        if (lines.length === 0) continue;
        for (const line of lines) {
          for (const odd of line.Odds ?? []) {
            const specifierOk =
              !bet.Specifier ||
              bet.Specifier === '' ||
              normalizeSpecifier(bet.Specifier) === normalizeSpecifier(line.Specifier);
            if (
              marketIdMatches(bet.MarketId, market.Id) &&
              specifierOk &&
              bet.Outcome === odd.Outcome
            ) {
              found = true;
              const marketId = market.Id;
              if (
                line.Active === true &&
                odd.Active === false &&
                (marketId === 1 || marketId === 10)
              ) {
                bet.Probability = 1;
              } else if (odd.Value === 0) {
                bet.Probability = null;
              } else if (odd.Value !== 1) {
                bet.Odd = roundOdd(odd.Value);
                bet.Probability = 1 / bet.Odd;
              }
              break;
            }
          }
          if (found) break;
        }
        if (found) break;
      }
      if (!found) {
        bet.Probability = null;
      }
    }
  }

  return getTicketCashoutValue(ticket, settings);
}

/** Merge cashoutlist response Items into an open ticket and compute offer. */
export function offerFromCashoutListTicket(
  ticket: OpenCashoutTicket,
  responseItems: CashoutTicketItem[] | undefined,
  settings: CashoutSettings | null | undefined,
): number {
  if (responseItems?.length) {
    ticket.Items = responseItems;
  }
  // PlacedOdds must outlive Item replacement from cashoutlist.
  if (!ticket.PlacedOdds || Object.keys(ticket.PlacedOdds).length === 0) {
    ticket.PlacedOdds = getPlacedOdds(ticket.Items);
  }
  roundTicketOdds(ticket.Items);
  return getTicketCashoutValue(ticket, settings);
}

export function partialCashoutAmounts(
  fullOffer: number,
  stake: number,
  percent: number,
): { stakeUsed: number; partialWin: number } {
  const clamped = Math.min(
    PARTIAL_CASHOUT_MAX_PERCENT,
    Math.max(PARTIAL_CASHOUT_MIN_PERCENT, percent),
  );
  const stakeUsed = Math.floor((stake * (clamped / 100) + Number.EPSILON) * 100) / 100;
  const partialWin =
    Math.floor((fullOffer * (clamped / 100) + Number.EPSILON) * 100) / 100;
  return { stakeUsed, partialWin };
}
